<script>
import { useAlert } from 'dashboard/composables';
import CRMProductsAPI from 'dashboard/api/crm/products.js';
import DealFormField from 'dashboard/components/crm/DealFormField.vue';
import Modal from 'dashboard/components/Modal.vue';

export default {
  name: 'DealStageAttributes',
  components: { DealFormField, Modal },
  props: {
    deal: { type: Object, required: true },
  },
  emits: ['close'],
  data() {
    return {
      showModal: false,
      activeTransitionKey: null,
      editData: {},
      isSaving: false,
      availableProducts: [],
    };
  },
  computed: {
    isComexContact() {
      if (!this.deal.primary_contact) return false;
      const hasComexLabel = this.deal.primary_contact.labels?.includes('comex');
      const hasInternationalPhone =
        this.deal.primary_contact.phone_number &&
        !this.deal.primary_contact.phone_number.startsWith('+54');
      return hasComexLabel || hasInternationalPhone;
    },
    // All stage_transition_* entries that have non-empty data, newest first
    allTransitions() {
      if (!this.deal.custom_attributes) return [];
      return Object.entries(this.deal.custom_attributes)
        .filter(
          ([key, val]) =>
            key.startsWith('stage_transition_') &&
            val &&
            val.data &&
            Object.keys(val.data).length > 0
        )
        .map(([key, value]) => ({
          key,
          stageType: key.replace('stage_transition_', ''),
          ...value,
        }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    },
    activeTransition() {
      if (!this.activeTransitionKey) return null;
      return this.deal.custom_attributes?.[this.activeTransitionKey] || null;
    },
    activeFormFields() {
      return this.resolveFields(this.activeTransition);
    },
    hasAnyProductField() {
      return this.allTransitions.some(t => {
        const transition = this.deal.custom_attributes?.[t.key];
        return this.resolveFields(transition).some(
          f => f.updates_deal_field === 'product_ids'
        );
      });
    },
    // true cuando el modal está abierto pero no hay fields resolubles
    activeTransitionHasNoFields() {
      return this.showModal && this.activeFormFields.length === 0;
    },
  },
  async mounted() {
    if (this.hasAnyProductField) {
      await this.loadProducts();
    }
  },
  methods: {
    // Resuelve los fields de una transición con fallback de 3 niveles:
    // 1. form_config en el snapshot (deals nuevos)
    // 2. form_config del stage en stagesById (deals históricos, stage cargado)
    // 3. [] (stage eliminado o pipeline no cargado)
    resolveFields(transition) {
      if (!transition) return [];
      // Nivel 1: snapshot propio
      if (transition.form_config?.fields?.length) {
        return transition.form_config.fields;
      }
      // Nivel 2: stage actual en el store
      const toStageId = transition.to_stage_id;
      if (toStageId) {
        const stage = this.$store.state.crmBoard.stagesById[toStageId];
        if (stage?.form_config?.fields?.length) {
          return stage.form_config.fields;
        }
      }
      // Nivel 3: sin configuración disponible
      return [];
    },
    canViewTransition(key) {
      const transition = this.deal.custom_attributes?.[key];
      return this.resolveFields(transition).length > 0;
    },
    openModal(key) {
      this.activeTransitionKey = key;
      const transition = this.deal.custom_attributes?.[key];
      this.editData = JSON.parse(JSON.stringify(transition?.data || {}));
      // Carga lazy de productos si esta transición tiene un campo de productos
      const fields = this.resolveFields(transition);
      if (fields.some(f => f.updates_deal_field === 'product_ids') && !this.availableProducts.length) {
        this.loadProducts();
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.activeTransitionKey = null;
      this.editData = {};
    },
    async saveModal() {
      if (!this.activeTransitionKey || !this.activeTransition) return;
      this.isSaving = true;
      try {
        // 1. Actualizar snapshot del historial de etapa en custom_attributes
        const updatedCustomAttributes = {
          ...(this.deal.custom_attributes || {}),
          [this.activeTransitionKey]: {
            ...this.activeTransition,
            data: { ...this.editData },
          },
        };

        // 2. Extraer campos que actualizan directamente columnas del deal
        //    Misma lógica que DealMoveForm.handleSubmit para consistencia
        const dealUpdates = {};
        this.activeFormFields.forEach(field => {
          if (!field.updates_deal_field) return;
          const value = this.editData[field.key];
          if (value === undefined || value === null) return;

          if (field.updates_deal_field === 'amount_cents') {
            // editData almacena el valor en pesos (el form pre-popula amount_cents / 100).
            // parseFloat maneja tanto number como string por si la serialización lo convierte.
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              dealUpdates.amount_cents = Math.round(numValue * 100);
            }
          } else if (
            field.updates_deal_field === 'product_ids' ||
            field.updates_deal_field === 'payment_method_ids'
          ) {
            // editData almacena [{id, name}, ...]; enviar solo IDs al backend
            if (Array.isArray(value) && value.length > 0) {
              dealUpdates[field.updates_deal_field] = value.map(item =>
                typeof item === 'object' && item !== null ? item.id : item
              );
            }
          } else {
            dealUpdates[field.updates_deal_field] = value;
          }
        });

        // 3. Un solo PATCH: custom_attributes + campos del deal juntos
        await this.$store.dispatch('crmBoard/updateDeal', {
          dealId: this.deal.id,
          dealData: {
            custom_attributes: updatedCustomAttributes,
            ...dealUpdates,
          },
        });
        this.showModal = false;
        this.activeTransitionKey = null;
        this.editData = {};
        useAlert(this.$t('CRM.DEAL_STAGE_ATTRIBUTES.SAVE_SUCCESS'));
        this.$emit('close');
      } catch {
        useAlert(this.$t('CRM.DEAL_STAGE_ATTRIBUTES.SAVE_ERROR'));
      } finally {
        this.isSaving = false;
      }
    },
    async loadProducts() {
      try {
        const response = await CRMProductsAPI.get();
        this.availableProducts = response.data || [];
      } catch {
        this.availableProducts = [];
      }
    },
    setFieldValue(fieldKey, value) {
      this.editData[fieldKey] = value;
    },
    setOtherSpecifyValue(fieldKey, value) {
      this.editData[`${fieldKey}_other_specify`] = value;
    },
    getOtherSpecifyValue(fieldKey) {
      return this.editData[`${fieldKey}_other_specify`] || '';
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      try {
        return new Date(timestamp).toLocaleDateString('es-AR', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return timestamp;
      }
    },
    getStageTypeName(stageType) {
      const names = {
        won: 'Ganado',
        lost: 'Perdido',
        paused: 'Pausado',
        canceled: 'Cancelado',
        regular: 'Normal',
      };
      return names[stageType] || stageType;
    },
  },
};
</script>

<template>
  <div v-if="allTransitions.length > 0" class="border-t border-n-weak pt-5 mt-5">
    <!-- Section header -->
    <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
      {{ $t('CRM.DEAL_STAGE_ATTRIBUTES.TITLE') }}
    </h4>

    <!-- History list -->
    <div class="space-y-2">
      <div
        v-for="transition in allTransitions"
        :key="transition.key"
        class="flex items-center justify-between py-2 px-3 rounded-lg bg-n-slate-3/30 hover:bg-n-slate-3/50 transition-colors"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ getStageTypeName(transition.stageType) }}
          </span>
          <span class="text-xs text-n-slate-11 mt-0.5">
            {{ formatTimestamp(transition.timestamp) }}
          </span>
        </div>
        <button
          type="button"
          :disabled="!canViewTransition(transition.key)"
          :title="!canViewTransition(transition.key) ? $t('CRM.DEAL_STAGE_ATTRIBUTES.NO_CONFIG') : undefined"
          class="ml-3 flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border rounded-md transition-colors"
          :class="canViewTransition(transition.key)
            ? 'text-woot-600 dark:text-woot-400 border-woot-300 dark:border-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 cursor-pointer'
            : 'text-n-slate-9 border-n-slate-6 cursor-not-allowed opacity-50'"
          @click="canViewTransition(transition.key) && openModal(transition.key)"
        >
          <fluent-icon icon="eye" size="12" />
          {{ $t('CRM.DEAL_STAGE_ATTRIBUTES.VIEW') }}
        </button>
      </div>
    </div>

    <!-- View/Edit modal -->
    <Modal v-model:show="showModal" :on-close="closeModal">
      <div class="p-6">
        <!-- Modal header -->
        <div class="mb-4">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ getStageTypeName(activeTransition?.stageType || activeTransitionKey?.replace('stage_transition_', '')) }}
          </h3>
          <p v-if="activeTransition?.timestamp" class="text-xs text-n-slate-11 mt-0.5">
            {{ formatTimestamp(activeTransition.timestamp) }}
          </p>
        </div>

        <!-- Fields -->
        <div v-if="activeFormFields.length > 0" class="space-y-4">
          <!-- fallback indicator: snapshot sin form_config, usando stage actual -->
          <p
            v-if="!activeTransition?.form_config?.fields?.length"
            class="text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 rounded px-2 py-1"
          >
            {{ $t('CRM.DEAL_STAGE_ATTRIBUTES.FALLBACK_WARNING') }}
          </p>
          <div v-for="field in activeFormFields" :key="field.key">
            <label
              :for="`modal-field-${field.key}`"
              class="block text-sm font-medium text-n-slate-12 mb-1"
            >
              {{ field.label }}
            </label>
            <DealFormField
              :field="{ ...field, key: `modal-field-${field.key}` }"
              :model-value="editData[field.key]"
              :other-specify-value="getOtherSpecifyValue(field.key)"
              :readonly="false"
              :available-products="availableProducts"
              :is-comex-contact="isComexContact"
              @update:model-value="setFieldValue(field.key, $event)"
              @update:other-specify-value="setOtherSpecifyValue(field.key, $event)"
            />
          </div>
        </div>

        <!-- Footer buttons -->
        <div class="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-n-weak">
          <button
            type="button"
            :disabled="isSaving"
            class="px-3 py-1.5 text-sm font-medium text-n-slate-12 hover:bg-n-slate-3/50 rounded-md transition-colors disabled:opacity-50"
            @click="closeModal"
          >
            {{ $t('CRM.DEAL_STAGE_ATTRIBUTES.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="isSaving"
            class="px-3 py-1.5 text-sm font-medium text-white bg-n-brand rounded-md hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-all"
            @click="saveModal"
          >
            <span
              v-if="isSaving"
              class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"
            />
            {{ isSaving ? $t('CRM.DEAL_STAGE_ATTRIBUTES.SAVING') : $t('CRM.DEAL_STAGE_ATTRIBUTES.SAVE') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
/* Prefer Tailwind */
</style>
